// The Jobs pages' reads and rules: ONE copy, used by the desktop Jobs pages (pages/JobsPage.js,
// pages/JobsDetailsPage.js) and the phone (mobile/screens/JobsScreen.jsx, JobDetailScreen.jsx).
// docs/mobile-pwa-plan.md "One copy of every action". Plain functions, no React.
import config from "../../config.js";
import { fetchAllJobPages, fetchJobsPage } from "../../utils/jobPages";

const API_BASE = config.API_BASE_URL;

/** The statuses a job can be set to by hand (Cancel is its own desktop action). */
export const JOB_STATUS_OPTIONS = Object.freeze(["Pending", "In Progress", "Paused", "Completed", "Shipped"]);

/** Priority levels with the job detail page's labels. */
export const JOB_PRIORITY_OPTIONS = Object.freeze([
  { value: 1, label: "1 (Critical)" },
  { value: 2, label: "2 (High)" },
  { value: 3, label: "3 (Normal)" },
  { value: 4, label: "4 (Low)" },
]);

/** The stations the list's "queue" filter offers (GET /jobs queue_filter). */
export const JOB_LIST_STATIONS = Object.freeze(["Laser", "Tap", "Brake", "Fabrication", "Blast", "Finish", "Shipping"]);

/** A closed job's due date is not edited (the list's rule). */
export function canEditDueDate(job) {
  return !["Completed", "Cancelled", "Shipped"].includes(job && job.status);
}

/** Pre-ship is offered while the job is still being made. */
export function canPreShip(job) {
  return ["Pending", "In Progress", "Paused"].includes(job && job.status);
}

/** The list's default "since" date: 30 days back. */
export function defaultListStartDate(now = new Date()) {
  const date = new Date(now.getTime());
  date.setDate(date.getDate() - 30);
  return date;
}

/**
 * GET /jobs query for the list. A search term searches everything (the other filters are off while
 * searching); "hide shipped" applies either way. `summary` asks for job fields only (the phone's cards).
 */
export function jobListParams({ searchTerm, customerId, customerPo, customerSo, startDate, queueFilter, hideShipped, summary = false }) {
  const params = new URLSearchParams();
  if (searchTerm) {
    params.append("search_term", searchTerm);
  } else {
    if (customerId) params.append("customer_id", customerId);
    if (customerPo) params.append("customer_po", customerPo);
    if (customerSo) params.append("customer_so", customerSo);
    if (startDate) {
      const yyyy = startDate.getFullYear();
      const mm = String(startDate.getMonth() + 1).padStart(2, "0");
      const dd = String(startDate.getDate()).padStart(2, "0");
      params.append("start_date", `${yyyy}-${mm}-${dd}`);
    }
    if (queueFilter) params.append("queue_filter", queueFilter);
  }
  if (hideShipped) params.append("status", "Active");
  // Job fields only, no tasks / parts: a screen that shows cards (CLAUDE.md "GET /jobs loads page by page").
  if (summary) params.append("summary", "true");
  return params;
}

/** Every page of GET /jobs for that query (utils/jobPages.js). null when cancelled. */
export function loadJobList(params, { isCancelled } = {}) {
  return fetchAllJobPages((page) => fetchJobsPage(API_BASE, params, page), { isCancelled });
}

/** The list's order: by a column, ascending or descending (the desktop table's header sort). */
export function sortJobs(jobs, sortColumn, sortOrder) {
  return [...(jobs || [])].sort((a, b) => {
    let valA;
    let valB;
    switch (sortColumn) {
      case "master_job_num": valA = a.master_job_num || ""; valB = b.master_job_num || ""; break;
      case "job_id": valA = a.job_id; valB = b.job_id; break;
      case "job_description": valA = a.job_description || ""; valB = b.job_description || ""; break;
      case "status": valA = a.status || ""; valB = b.status || ""; break;
      case "priority_level": valA = a.priority_level || 0; valB = b.priority_level || 0; break;
      case "due_date": valA = a.due_date ? new Date(a.due_date).getTime() : 0; valB = b.due_date ? new Date(b.due_date).getTime() : 0; break;
      default: return 0;
    }
    if (valA < valB) return sortOrder === "asc" ? -1 : 1;
    if (valA > valB) return sortOrder === "asc" ? 1 : -1;
    return 0;
  });
}

/** GET /customers, by name. Throws Error on a failed request. */
export async function loadCustomersByName() {
  const res = await fetch(`${API_BASE}/customers`);
  if (!res.ok) throw new Error(`Error fetching customers: ${res.status}`);
  return (await res.json()).sort((a, b) => a.name.localeCompare(b.name));
}

/** GET /jobs/{id}. Throws Error("Server error: <status>") when it can't be read. */
export async function loadJob(jobId) {
  const res = await fetch(`${API_BASE}/jobs/${jobId}`);
  if (!res.ok) throw new Error(`Server error: ${res.status}`);
  return res.json();
}

/** GET /jobs/{id}/progress → its tasks (null when the answer has none). Throws on a failed request. */
export async function loadJobTasks(jobId) {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/progress`);
  if (!res.ok) throw new Error(`Tasks fetch error: ${res.status}`);
  const data = await res.json();
  return data && data.tasks ? data.tasks : null;
}

/** GET /jobs/{id}/documents (the job's files: PO, drawings, photos). null when they can't be read. */
export async function loadJobDocuments(jobId) {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/documents`);
  if (!res.ok) return null;
  return res.json();
}

/** GET /jobs/{id}/parts for Pre-ship (ordered / shipped per part). Throws when it can't be read. */
export async function loadShipParts(jobId) {
  const res = await fetch(`${API_BASE}/jobs/${jobId}/parts`);
  if (!res.ok) throw new Error(`Failed to load parts (${res.status})`);
  const data = await res.json();
  return Array.isArray(data) ? data : (data.parts || []);
}

/** "3 days overdue" / "Due today" / "Due in 4 days", with its colour; null without a date. */
export function relativeDueLabel(dateStr, now = new Date()) {
  if (!dateStr) return null;
  const dueParts = String(dateStr).split("T")[0].split("-");
  const dueDate = new Date(parseInt(dueParts[0], 10), parseInt(dueParts[1], 10) - 1, parseInt(dueParts[2], 10));
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const diffDays = Math.round((dueDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
  if (diffDays < -1) return { text: `${Math.abs(diffDays)} days overdue`, color: "#dc3545" };
  if (diffDays === -1) return { text: "1 day overdue", color: "#dc3545" };
  if (diffDays === 0) return { text: "Due today", color: "#fd7e14" };
  if (diffDays === 1) return { text: "Due tomorrow", color: "#0d6efd" };
  if (diffDays <= 7) return { text: `Due in ${diffDays} days`, color: "#198754" };
  return { text: `Due in ${diffDays} days`, color: "#6c757d" };
}
